import React, { useState, useEffect } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Paper,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
} from "@mui/material";
import "./DisplayCategory.css";

const DisplayCategory = () => {
  const API_URL = process.env.REACT_APP_API_URL;
  const [recipes, setRecipes] = useState([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);

  const [openDetailsDialog, setOpenDetailsDialog] = useState(false);
  const [openEditDialog, setOpenEditDialog] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);

  const [selectedRecipe, setSelectedRecipe] = useState(null);
  const [formData, setFormData] = useState({});

  useEffect(() => {
    fetchRecipes();
  }, []);

  const fetchRecipes = async () => {
    try {
      const response = await fetch(`${API_URL}/category/getall`); // Replace with your backend endpoint
      const data = await response.json();
      setRecipes(data.results);
    } catch (error) {
      console.error("Error fetching recipes:", error);
    }
  };

  const handlePageChange = (event, newPage) => {
    setPage(newPage);
  };

  const handleRowsPerPageChange = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleOpenDetailsDialog = (recipe) => {
    setSelectedRecipe(recipe);
    setOpenDetailsDialog(true);
  };

  const handleOpenEditDialog = (recipe) => {
    setSelectedRecipe(recipe);
    setFormData({ ...recipe });
    setOpenEditDialog(true);
  };

  const handleOpenDeleteDialog = (recipe) => {
    setSelectedRecipe(recipe);
    setOpenDeleteDialog(true);
  };

  const handleCloseDialogs = () => {
    setOpenDetailsDialog(false);
    setOpenEditDialog(false);
    setOpenDeleteDialog(false);
    setSelectedRecipe(null);
    setFormData({});
  };

  const handleEditSubmit = async () => {
    try {
      const response = await fetch(
        `${API_URL}/category/updatecategory/${selectedRecipe.category_id}`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(formData),
        }
      );

      if (!response.ok) {
        throw new Error("Failed to update recipe");
      }

      const updatedRecipe = await response.json();
      setRecipes((prev) =>
        prev.map((recipe) =>
          recipe.category_id === updatedRecipe.category_id
            ? updatedRecipe
            : recipe
        )
      );
      console.log("Recipe updated:", updatedRecipe);
    } catch (error) {
      console.error("Error updating recipe:", error);
    } finally {
      handleCloseDialogs();
    }
  };

  const handleDeleteSubmit = async () => {
    try {
      const response = await fetch(
        `${API_URL}/category/deletecategory/${selectedRecipe.category_id}`,
        {
          method: "DELETE",
        }
      );

      if (!response.ok) {
        throw new Error("Failed to delete recipe");
      }

      setRecipes((prev) =>
        prev.filter(
          (recipe) => recipe.category_id !== selectedRecipe.category_id
        )
      );
      console.log("Recipe deleted:", selectedRecipe);
    } catch (error) {
      console.error("Error deleting recipe:", error);
    } finally {
      handleCloseDialogs();
    }
  };

  const handleInputChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  return (
    <>
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>ID</TableCell>
              <TableCell>Category</TableCell>
              <TableCell>Image url</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {recipes
              .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
              .map((recipe) => (
                <TableRow key={recipe.category_id}>
                  <TableCell>{recipe.category_id}</TableCell>
                  <TableCell>{recipe.category_name}</TableCell>
                  <TableCell className="url-truncate">{recipe.url}</TableCell>
                  <TableCell>
                    <Button
                      variant="contained"
                      color="primary"
                      size="small"
                      onClick={() => handleOpenEditDialog(recipe)}
                      sx={{ marginRight: 1 }}
                    >
                      Edit
                    </Button>
                    <Button
                      variant="contained"
                      color="error"
                      size="small"
                      onClick={() => handleOpenDeleteDialog(recipe)}
                      sx={{ marginRight: 1 }}
                    >
                      Delete
                    </Button>
                    <Button
                      variant="outlined"
                      color="info"
                      size="small"
                      onClick={() => handleOpenDetailsDialog(recipe)}
                    >
                      See Details
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
          </TableBody>
        </Table>
        <TablePagination
          rowsPerPageOptions={[5, 10, 25]}
          component="div"
          count={recipes.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handlePageChange}
          onRowsPerPageChange={handleRowsPerPageChange}
        />
      </TableContainer>

      {/* See Details Dialog */}
      <Dialog open={openDetailsDialog} onClose={handleCloseDialogs}>
        <DialogTitle>Recipe Details</DialogTitle>
        <DialogContent>
          <Typography variant="body1">
            <b>ID:</b> {selectedRecipe?.category_id}
          </Typography>
          <Typography variant="body1">
            <b>User:</b> {selectedRecipe?.user_id}
          </Typography>
          <Typography variant="body1">
            <b>Category:</b> {selectedRecipe?.category_name}
          </Typography>
          <Typography variant="body1">
            <b>Image url:</b> {selectedRecipe?.url}
          </Typography>

          <Typography variant="body1">
            <b>Count:</b> {selectedRecipe?.count}
          </Typography>
          <Typography variant="body1">
            <b>Created Date:</b> {selectedRecipe?.created_at}
          </Typography>
          <Typography variant="body1">
            <b>Updated Date:</b> {selectedRecipe?.updated_at}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialogs} color="primary">
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={openEditDialog} onClose={handleCloseDialogs}>
        <DialogTitle>Edit Recipe</DialogTitle>
        <DialogContent>
          <TextField
            label="User"
            name="user_id"
            value={formData.user_id || ""}
            onChange={handleInputChange}
            fullWidth
            margin="dense"
          />
          <TextField
            label="CATEGORY"
            name="category_name"
            value={formData.category_name || ""}
            onChange={handleInputChange}
            fullWidth
            margin="dense"
          />
          <TextField
            label="Image"
            name="url"
            value={formData.url || ""}
            onChange={handleInputChange}
            fullWidth
            margin="dense"
          />
          <TextField
            label="Count"
            name="count"
            value={formData.count || ""}
            onChange={handleInputChange}
            fullWidth
            margin="dense"
          />
          <TextField
            label="Created Date"
            name="created_at"
            value={formData.created_at || ""}
            onChange={handleInputChange}
            fullWidth
            margin="dense"
          />

          <TextField
            label="Updated Date"
            name="updated_at"
            value={formData.updated_at || ""}
            onChange={handleInputChange}
            fullWidth
            margin="dense"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialogs} color="secondary">
            Cancel
          </Button>
          <Button onClick={handleEditSubmit} color="primary">
            Save
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Dialog */}
      <Dialog open={openDeleteDialog} onClose={handleCloseDialogs}>
        <DialogTitle>Confirm Deletion</DialogTitle>
        <DialogContent>
          Are you sure you want to delete the recipe{" "}
          <b>{selectedRecipe?.category_name}</b>?
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialogs} color="secondary">
            Cancel
          </Button>
          <Button onClick={handleDeleteSubmit} color="error">
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default DisplayCategory;
